/**
 * Emit hosts for the `auth` analytics group, lifted out of the analytics
 * model's effects so a call site can emit without holding a Rematch `dispatch`.
 * Every payload is the effect's own.
 *
 * The hosts that tag an event with the auth provider read it from the store at
 * emit time through `readStoreAuthProviderForAnalytics`, exactly as the model
 * effects did, so no call site passes the provider in.
 */
import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags';
import { segmentClient } from '@/lib/analytics/legacy-sender-context';
import { pushGtmDataLayerEvent } from '@/lib/analytics/pushGtmDataLayerEvent';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { readStoreAuthProviderForAnalytics } from '@/lib/auth/effectiveAuthProvider';
import { clearRemoteConfigCustomSignals } from '@/lib/firebase/custom-signals';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type IntegrationType } from '@/models/analytics/features/integrations';
import { AuthenticationFlow } from '@/models/analytics/features/onboarding';
import {
  type AuthType,
  buildAuthenticationClickedProps,
  mapAuthProviderToProvider,
  mapSignInMethodToAuthType,
  type SignInMethod,
  SignOutLocation,
} from '@/models/analytics/features/user';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type AuthProvider } from '@/models/authModel';
import { logger } from '@/utils/logger/logger';
import { setSignInMethod } from '@/utils/signInSessionTracking';
import { getLandingUrl, getUtmParams } from '@/utils/utmTracking';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser';
import { setAnalyticsContext } from '@heidi/runtime/analytics';
import { type TeamModel } from '@heidi/workspace/contracts';
import posthog from 'posthog-js';

export function resetIdentity() {
  void segmentClient.reset();
  // Clear every RC custom signal so the next session is not targeted as the prior user.
  // The Firebase SDK loads lazily inside the custom-signals helper, not at import time.
  clearRemoteConfigCustomSignals();
}

export function setActiveTeam(team: TeamModel) {
  // DEV-4675 - remove user role list and change joined_team_members to a num type
  const teamModified = {
    ...team,
    joined_team_members: team.joined_team_members?.length ?? 0,
    team_members: team.team_members?.map((member) => ({
      ...member,
      user_role_list: member.user_role_list?.map((role) => ({
        id: role.id,
        name: role.name,
        config: role.config,
      })),
    })),
  };

  // Store the team id in the @heidi/runtime/analytics context registry — the
  // single teamId source for every destination. The caller only reaches here
  // with the team selected for the active workspace (selectActiveTeamId), so
  // this id is always correct for the current workspace; consumers read it
  // directly with no per-event guard.
  setAnalyticsContext({ teamId: team.id });
  // This is for autocaptured PH events
  posthog.group('team', team.id, teamModified);
  // This is for our custom events we send via segment
  void segmentClient.group(team.id, teamModified);

  // Set Amplitude group for team-based analytics
  try {
    amplitude.setGroup('team', team.id);
    const groupIdentifyObj = new AmplitudeIdentify();
    groupIdentifyObj.set('team_name', team.name ?? '');
    groupIdentifyObj.set('joined_team_members', teamModified.joined_team_members);
    amplitude.groupIdentify('team', team.id, groupIdentifyObj);
  } catch (error) {
    logger.error('[Amplitude] Failed to set group identity:', error);
  }
}

export function setAuthTypeUserProperty(authType: AuthType) {
  const identifyObj = new AmplitudeIdentify();
  identifyObj.set('auth_type', authType);
  amplitude.identify(identifyObj);
}

export function trackNativeAuthenticated() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NATIVE_AUTHENTICATED,
    },
  });
}

export function trackSignInSuccessfulWebAuth(input: {
  auth_provider: AuthProvider;
  sign_in_method: SignInMethod;
}) {
  // Track the sign in event
  track({
    event_name: TrackingEventName.UserSignedIn,
    props: {
      auth_provider: input.auth_provider,
      sign_in_method: input.sign_in_method,
      provider: mapAuthProviderToProvider(input.auth_provider),
      auth_type: mapSignInMethodToAuthType(input.sign_in_method),
    },
    options: { amplitude: false, posthog: true },
  });
}

export function trackSignUpSuccessfulWebAuth(input: {
  auth_provider: AuthProvider;
  sign_in_method: SignInMethod;
}) {
  track({
    event_name: TrackingEventName.UserSignedUp,
    props: {
      auth_provider: input.auth_provider,
      sign_in_method: input.sign_in_method,
      provider: mapAuthProviderToProvider(input.auth_provider),
      auth_type: mapSignInMethodToAuthType(input.sign_in_method),
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    },
    options: { amplitude: false, posthog: true },
  });
}

export function signInClicked(
  sign_in_method?: 'email' | 'google' | 'microsoft' | 'apple'
) {
  const authType = mapSignInMethodToAuthType(sign_in_method);

  trackLegacyEvent({
    name: SegmentObjectName.SignIn,
    props: {
      action: SegmentActionName.SignInClicked,
      sign_in_method,
    },
  });

  // V2 — authentication_clicked
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AuthenticationClicked,
    props: buildAuthenticationClickedProps({
      clickAction: sign_in_method,
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    }),
  });

  if (authType) {
    setAuthTypeUserProperty(authType);
  }

  setSignInMethod(sign_in_method ?? '');
}

export function trackSignUpSuccessfulWidgetAuth(input: {
  integration_type: IntegrationType;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.UserSignedUp,
    props: {
      integration_type: input.integration_type,
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    },
  });
}

export function trackSignInSuccessfulWidgetAuth(input: {
  integration_type: IntegrationType;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.UserSignedIn,
    props: {
      integration_type: input.integration_type,
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    },
  });
}

export function trackSignUpViaTeamInvite(input: { authID: string }) {
  const { authID } = input;

  trackLegacyEvent({
    name: SegmentObjectName.SignUp,
    props: {
      action: SegmentActionName.SignUpViaTeamInvite,
    },
  });

  posthog.identify(authID, {
    signed_up_via_invite: true,
    from: 'team-invite',
  });

  // V2 — user_signed_up (via team invite)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.UserSignedUp,
    props: {
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    },
  });
}

export function navigationLogout() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationLogout,
    },
  });

  track({
    event_name: TrackingEventName.UserSignedOut,
    props: {
      screen_section: SignOutLocation.NAVIGATION_SIDEBAR,
      auth_provider: readStoreAuthProviderForAnalytics(),
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    },
    options: { amplitude: false, posthog: true },
  });
}

export function navigationLogoutWidgetAuth() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.UserSignedOut,
    props: {
      screen_section: SignOutLocation.WIDGET_AUTH_PAGE,
      auth_provider: readStoreAuthProviderForAnalytics(),
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    },
  });
}

export function demoBindOnboardingInfo(sessionId?: string) {
  pushGtmDataLayerEvent({ event: 'signup_successful' });

  // Enhanced attribution: sessionStorage survives OAuth redirects and captures
  // click IDs (gclid/fbclid/…) beyond the current URL query string.
  const attributionParams = getUtmParams();
  // Original landing URL captured when the user first arrived with UTM params
  const landingUrl = getLandingUrl();

  trackLegacyEvent({
    name: SegmentObjectName.ProductTour,
    props: {
      action: SegmentActionName.DemoBindUserToOnboardingInfo,
      custom_utm_source: attributionParams.utm_source,
      sessionId,
      // Include all attribution params for easier querying
      ...attributionParams,
    },
    // Override page URL with the original landing URL to preserve UTM/click IDs after OAuth redirects
    pageUrl: landingUrl,
  });

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AuthenticationCompleted,
    props: {
      authentication_flow: AuthenticationFlow.SIGN_UP,
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
      custom_utm_source: attributionParams.utm_source,
      ...attributionParams,
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    },
    // Override page URL with the original landing URL to preserve UTM/click IDs after OAuth redirects
    pageUrl: landingUrl,
  });

  // V2 — product_tour_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ProductTourViewed,
    props: {
      tour_action: 'bind_user_to_onboarding_info',
    },
  });
}

export function extensionSetToken() {
  trackLegacyEvent({
    name: SegmentObjectName.ChromeExtension,
    props: {
      action: SegmentActionName.ExtensionSetToken,
    },
  });

  // V2 — authentication_completed (chrome extension)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AuthenticationCompleted,
    props: {
      authentication_flow: AuthenticationFlow.LOGIN,
      provider: mapAuthProviderToProvider(readStoreAuthProviderForAnalytics()),
    },
  });
}
